/**
 * App-wide telemetry singleton.
 *
 * Initialises the shared `@itoresearch/observability` client once and exposes
 * it to every module that needs to ship structured events to Axiom (boot,
 * router, API client, global error capture). When `AXIOM_TOKEN` /
 * `AXIOM_DATASET` are absent the client degrades to console-only output and
 * nothing fails.
 */

import { initTelemetry, type Telemetry } from "@itoresearch/observability";

export const telemetry: Telemetry = initTelemetry("web", "0.1.0");

/**
 * Forward uncaught errors and unhandled promise rejections to Axiom.
 *
 * Registers `error` + `unhandledrejection` listeners once per document. Safe
 * to call multiple times (listeners are registered only if not present) and a
 * no-op outside the browser.
 */
export function installGlobalErrorCapture(): void {
  if (typeof window === "undefined") return;

  if (!("__itoErrorCaptureInstalled" in window)) {
    Object.defineProperty(window, "__itoErrorCaptureInstalled", {
      value: true,
    });
    window.addEventListener("error", (event) => {
      telemetry.error(event.message || "uncaught error", {
        kind: "window.error",
        filename: event.filename || undefined,
        lineno: event.lineno || undefined,
        colno: event.colno || undefined,
        // Error events from `onerror` carry the error object only in some
        // browsers; prefer its stack when available.
        stack: event.error instanceof Error ? event.error.stack : undefined,
      });
    });
    window.addEventListener("unhandledrejection", (event) => {
      const reason: unknown = event.reason;
      telemetry.error(
        reason instanceof Error ? reason.message : String(reason),
        {
          kind: "unhandledrejection",
          stack: reason instanceof Error ? reason.stack : undefined,
        },
      );
    });
  }
}
